import type { HistoryListItemProps } from './HistoryListItem';

import { useCallback, useEffect, useRef } from 'react';

import { RefreshControl, StyleSheet } from 'react-native';

import { useFocusEffect } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { getColorTokenValue } from '@utility-nyc/react-native-ui-config';
import { FlatList } from 'react-native-gesture-handler';
import { useMedia } from 'tamagui';

import { Toast } from '@shared/utils';
import { ScreenWrapper } from '@south-street-app/molecules';
import {
  useGetAnalyticFirmAssociates,
  useGetEndUserTradeHistory,
} from '@utility-nyc/react-query-sdk';

import { HistoryListEmptyComponent } from './HistoryListEmptyComponent';
import { HistoryListItem } from './HistoryListItem';
import { HistoryListLoadingComponent } from './HistoryListLoadingComponent';

const SENTRY_TAG_TYPE = 'screen';
const SENTRY_TAG_CONTEXT = 'HistoryScreen';

const styles = StyleSheet.create({
  desktopContainer: {
    width: 800,
    alignSelf: 'center',
  },
  sideBorders: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: getColorTokenValue('$mono300'),
  },
});

const HistoryScreen = () => {
  const { desktop } = useMedia();
  const { data, error, isLoading, isRefetching, refetch } =
    useGetEndUserTradeHistory();
  const { isLoading: isAssociatesLoading } = useGetAnalyticFirmAssociates();
  const lastErrorRef = useRef<unknown>(undefined);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  useEffect(() => {
    if (!error || lastErrorRef.current === error) {
      return;
    }

    lastErrorRef.current = error;

    Sentry.captureException(error, {
      tags: {
        type: SENTRY_TAG_TYPE,
        context: SENTRY_TAG_CONTEXT,
        method: 'useGetEndUserTradeHistory',
      },
    });

    Toast.showErrorToast({
      text1: 'Could not load history',
      text2: 'Please try again.',
    });
  }, [error]);

  const renderItem = useCallback(
    ({ item }: HistoryListItemProps) => <HistoryListItem item={item} />,
    [],
  );

  const EmptyComponent = useCallback(() => <HistoryListEmptyComponent />, []);

  const LoadingComponent = useCallback(
    () => <HistoryListLoadingComponent />,
    [],
  );
  const isScreenLoading = isLoading || isAssociatesLoading;

  const onRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <ScreenWrapper>
      <FlatList
        data={isScreenLoading ? [] : data?.data.tradeRequests}
        renderItem={renderItem}
        ListEmptyComponent={isScreenLoading ? LoadingComponent : EmptyComponent}
        contentContainerStyle={[
          desktop ? styles.desktopContainer : undefined,
          !isScreenLoading && data?.data.tradeRequests
            ? styles.sideBorders
            : undefined,
        ]}
        refreshControl={
          <RefreshControl refreshing={isRefetching} onRefresh={onRefresh} />
        }
      />
    </ScreenWrapper>
  );
};

export { HistoryScreen };
